import * as React from 'react';
import { GridHeaderFilterCellProps } from '@mui/x-data-grid-pro/components/headerFiltering/GridHeaderFilterCell';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import searchIcon from 'Assets/Icons/search.svg';
import { FilterContext } from '../DataGrid';
import './MultiSelect.scss';

export type SelectOption = string | { label: string; value: string };

const optionValue = (o: SelectOption) => (typeof o === 'string' ? o : o.value);
const optionLabel = (o: SelectOption) => (typeof o === 'string' ? o : o.label);

interface MultiSelectFilterCellProps extends GridHeaderFilterCellProps {
  optionItems?: SelectOption[];
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const normalizeValue = (v: any): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') return v.split('|').filter(Boolean);
  return [];
};

export const MultiSelectFilter = ({ item, optionItems }: MultiSelectFilterCellProps) => {
  const { values, onChange, disabledFields } = React.useContext(FilterContext);
  const isDisabled = disabledFields?.has(item.field) ?? false;
  const contextRaw = values[item.field];

  const [pending, setPending] = React.useState<string[]>(() => normalizeValue(contextRaw));
  const [search, setSearch] = React.useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    setPending(normalizeValue(contextRaw));
  }, [contextRaw]);

  React.useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const applyChange = (next: string[]) => {
    setPending(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange(item.field, next.length ? next : undefined);
    }, 400);
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;
    applyChange(next);
  };

  const lowerSearch = search.toLowerCase();
  const filteredItems = optionItems?.filter((o) =>
    optionLabel(o).toLowerCase().includes(lowerSearch)
  );

  const allFilteredVals = filteredItems?.map(optionValue) ?? [];
  const allSelected = allFilteredVals.length > 0 && allFilteredVals.every(v => pending.includes(v));

  const handleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = allSelected
      ? pending.filter(v => !allFilteredVals.includes(v))
      : Array.from(new Set([...pending, ...allFilteredVals]));
    applyChange(next);
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    applyChange([]);
  };

  return (
    <div className={`filter-row${isDisabled ? ' filter-row--disabled' : ''}`}>
      <div className="multiselect-wrapper">
        <Select
        multiple
        disabled={isDisabled}
        value={pending}
        onChange={handleChange}
        displayEmpty
        renderValue={(selected) =>
          selected
            .map((v) => {
              const opt = optionItems?.find((o) => optionValue(o) === v);
              return opt ? optionLabel(opt) : v;
            })
            .join(', ')
        }
        onClose={() => setSearch('')}
        MenuProps={{
          disableAutoFocusItem: true,
          MenuListProps: {
            disableListWrap: true,
            sx: { '& .MuiMenuItem-root': { paddingTop: '10px', paddingBottom: '10px' } },
          },
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
              width: 280,
            },
          },
        }}
      >
        <ListSubheader sx={{ p: 2, lineHeight: 'normal', bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
          <div className="multiselect-search-box">
            <img src={searchIcon} alt="" className="multiselect-search-icon" />
            <input
              className="multiselect-search-input"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
            />
          </div>
        </ListSubheader>

        {filteredItems?.map((opt) => {
          const val = optionValue(opt);
          const label = optionLabel(opt);
          const selected = pending.includes(val);
          const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
          return (
            <MenuItem key={val} value={val}>
              <SelectionIcon fontSize="small" style={{ marginRight: 8 }} />
              <ListItemText primary={label} />
            </MenuItem>
          );
        })}

        <ListSubheader
          sx={{
            '&&': { position: 'sticky', bottom: 0, top: 'auto' },
            bgcolor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.12)',
            lineHeight: 'normal',
            p: 0,
            zIndex: 2,
          }}
        >
          <div
            className="multiselect-footer"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="multiselect-select-all" onClick={handleSelectAll}>
              {allSelected
                ? <CheckBoxIcon fontSize="small" color="primary" style={{ marginRight: 8 }} />
                : <CheckBoxOutlineBlankIcon fontSize="small" style={{ marginRight: 8 }} />
              }
              <span>Select All</span>
            </div>
            <button className="multiselect-reset-btn" type="button" onClick={handleReset}>
              Reset
            </button>
          </div>
        </ListSubheader>
      </Select>
      </div>

      {pending.length > 0 && (
        <IconButton size="small" onClick={handleReset} className="filter-clear-btn" tabIndex={-1}>
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

export default MultiSelectFilter;
